import { fmtMoney } from "@/lib/fmt";

/**
 * Table-cell components that colour-code their values.
 *
 * ROAS ≥ 1.5×  → green (val-pos)
 * ROAS < 0.8×  → red   (val-neg)
 * Between      → neutral
 *
 * CPA  < $1.50 → green
 * CPA  > $3.00 → red  (defaults; thresholds are per-column tunable)
 *
 * Each renders a <td>, which keeps the caller from having to wrap.
 */

export function RoasCell({ v }: { v: number | null | undefined }) {
  if (v === null || v === undefined) {
    return <td className="num val-muted">—</td>;
  }
  const cls = v >= 1.5 ? "val-pos" : v < 0.8 ? "val-neg" : "";
  return <td className={"num " + cls}>{v.toFixed(2)}×</td>;
}

export function CpaCell({
  v,
  good = 1.5,
  bad = 3,
}: {
  v: number | null | undefined;
  good?: number;
  bad?: number;
}) {
  if (v === null || v === undefined) {
    return <td className="num val-muted">—</td>;
  }
  const cls = v < good ? "val-pos" : v > bad ? "val-neg" : "";
  return <td className={"num " + cls}>${fmtMoney(v)}</td>;
}

/**
 * IPM badge — coloured rectangle around the per-mille install rate.
 * Thresholds (Apple-style, requested by user):
 *   v < 61       → red
 *   61 ≤ v < 90  → yellow
 *   90 ≤ v ≤ 400 → green
 *   v > 400      → green + 🔥
 * Renders an inline <span>, so it composes inside a `<td>` (data rows)
 * or directly into the totals `ReactNode` slot.
 */
export function IpmBadge({ v }: { v: number | null | undefined }) {
  if (v === null || v === undefined) return <span className="val-muted">—</span>;
  let tier: "red" | "yellow" | "green" | "fire";
  if (v < 61) tier = "red";
  else if (v < 90) tier = "yellow";
  else if (v <= 400) tier = "green";
  else tier = "fire";
  return (
    <span className={"ipm-badge ipm-badge-" + tier}>
      <span>{v.toFixed(2)}</span>
      {tier === "fire" && <span aria-hidden style={{ marginLeft: 4 }}>🔥</span>}
    </span>
  );
}
